import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  PowerIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Modal from "./components/ui/Modal";
import Table from "./components/ui/Table";
import Pagination from "./components/ui/Pagination";
import Spinner from "./components/ui/Spinner";
import ImageDropzone from "./components/ui/ImageDropzone";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const ASSET_HOST = baseUrl.replace("/admin", "");
const ITEMS_PER_PAGE = 10;

const TASK_TYPES = [
  { value: "telegram", label: "Telegram" },
  { value: "youtube", label: "YouTube" },
  { value: "download_app", label: "Download App" },
  { value: "website_visit", label: "Website Visit" },
];

const taskTypeLabel = (value) => TASK_TYPES.find((t) => t.value === value)?.label || value;

const emptyForm = {
  title: "",
  description: "",
  taskType: "",
  link: "",
  videoUrl: "",
  rewardCoin: "",
  visitDuration: "",
  image: null,
};

const resolveImage = (image) => {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  return `${ASSET_HOST}/${image.replace(/^\//, "")}`;
};

const TaskPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("tasks");
  const token = localStorage.getItem("token");

  // Tasks tab state
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [taskDetail, setTaskDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Completions tab state
  const [completions, setCompletions] = useState([]);
  const [completionsLoading, setCompletionsLoading] = useState(true);
  const [completionsError, setCompletionsError] = useState("");
  const [taskIdFilter, setTaskIdFilter] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [completionsPage, setCompletionsPage] = useState(1);
  const [completionsTotalPages, setCompletionsTotalPages] = useState(1);

  const fetchTasks = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (search.trim()) params.append("search", search.trim());
      if (taskTypeFilter) params.append("taskType", taskTypeFilter);
      if (isActiveFilter) params.append("isActive", isActiveFilter);

      const res = await fetch(`${baseUrl}/task/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status) {
        setTasks(result.data || []);
        setTotalPages(result.pagination?.lastPage || 1);
        setTotalItems(result.pagination?.total || 0);
        setCurrentPage(result.pagination?.currentPage || 1);
      } else {
        throw new Error(result.message || "Failed to fetch tasks");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletions = async (page = completionsPage) => {
    setCompletionsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (taskIdFilter) params.append("taskId", taskIdFilter);
      if (userIdFilter) params.append("userId", userIdFilter);

      const res = await fetch(`${baseUrl}/task/completions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status) {
        setCompletions(result.data || []);
        setCompletionsTotalPages(result.pagination?.lastPage || 1);
        setCompletionsPage(result.pagination?.currentPage || 1);
      } else {
        throw new Error(result.message || "Failed to fetch completions");
      }
    } catch (err) {
      setCompletionsError(err.message);
    } finally {
      setCompletionsLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, taskTypeFilter, isActiveFilter]);

  useEffect(() => {
    if (activeTab === "completions") {
      fetchCompletions(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, taskIdFilter, userIdFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleImageChange = (file) => {
    setFormData((prev) => ({ ...prev, image: file }));
    setFormErrors((prev) => ({ ...prev, image: "" }));
  };

  const validateForm = (isEdit = false) => {
    const errors = {};
    if (!formData.title || formData.title.trim().length < 3) {
      errors.title = "Title must be at least 3 characters";
    }
    if (!formData.taskType) {
      errors.taskType = "Task type is required";
    }
    if (!formData.rewardCoin || isNaN(formData.rewardCoin) || Number(formData.rewardCoin) <= 0) {
      errors.rewardCoin = "Reward coin must be a positive number";
    }
    if (formData.taskType === "website_visit" && (!formData.visitDuration || Number(formData.visitDuration) <= 0)) {
      errors.visitDuration = "Visit duration is required for website visit tasks";
    }
    if (!isEdit && !formData.image) {
      errors.image = "Image is required for new task";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildFormData = () => {
    const form = new FormData();
    form.append("title", formData.title);
    form.append("description", formData.description);
    form.append("taskType", formData.taskType);
    form.append("link", formData.link);
    form.append("videoUrl", formData.videoUrl);
    form.append("rewardCoin", formData.rewardCoin);
    form.append("visitDuration", formData.visitDuration || "");
    if (formData.image) form.append("image", formData.image);
    return form;
  };

  const handleAddNew = async () => {
    if (!validateForm(false)) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${baseUrl}/task/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: buildFormData(),
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Create failed");

      toast.success("Task created successfully!");
      setShowModal(false);
      setFormData(emptyForm);
      setEditingIndex(null);
      await fetchTasks(1);
    } catch (err) {
      toast.error(err.message || "Failed to create task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (index) => {
    const task = tasks[index];
    setEditingIndex(index);
    setFormData({
      title: task.title || "",
      description: task.description || "",
      taskType: task.taskType || "",
      link: task.link || "",
      videoUrl: task.videoUrl || "",
      rewardCoin: task.rewardCoin || "",
      visitDuration: task.visitDuration || "",
      image: null,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!validateForm(true)) return;
    setSubmitting(true);
    const current = tasks[editingIndex];
    try {
      const res = await fetch(`${baseUrl}/task/edit/${current.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: buildFormData(),
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Update failed");

      toast.success("Task updated successfully!");
      setShowModal(false);
      setFormData(emptyForm);
      setEditingIndex(null);
      await fetchTasks(currentPage);
    } catch (err) {
      toast.error(err.message || "Failed to update task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      const res = await fetch(`${baseUrl}/task/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Delete failed");

      toast.success("Task deleted successfully!");
      await fetchTasks(currentPage);
    } catch (err) {
      toast.error(err.message || "Failed to delete task");
    }
  };

  const viewTaskDetail = async (id) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    setTaskDetail(null);
    try {
      const res = await fetch(`${baseUrl}/task/details/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Failed to fetch task details");
      setTaskDetail(result.data);
    } catch (err) {
      toast.error(err.message || "Failed to fetch task details");
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setTaskDetail(null);
  };

  const toggleActive = async (task) => {
    try {
      const endpoint = task.isActive
        ? `${baseUrl}/task/deactivate/${task.id}`
        : `${baseUrl}/task/activate/${task.id}`;
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Update failed");

      toast.success(result.message || "Status updated");
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, isActive: !task.isActive } : t))
      );
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const taskColumns = [
    {
      key: "image",
      header: "Image",
      render: (task) =>
        resolveImage(task.image) ? (
          <img src={resolveImage(task.image)} alt={task.title} className="h-12 w-12 object-cover rounded" />
        ) : (
          <span className="text-gray-500 italic">No Image</span>
        ),
    },
    { key: "title", header: "Title" },
    {
      key: "taskType",
      header: "Type",
      render: (task) => (
        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-300">
          {taskTypeLabel(task.taskType)}
        </span>
      ),
    },
    { key: "rewardCoin", header: "Reward Coin" },
    {
      key: "visitDuration",
      header: "Visit Duration",
      render: (task) => (task.taskType === "website_visit" ? `${task.visitDuration}s` : "-"),
    },
    {
      key: "completedCount",
      header: "Completed",
      render: (task) => task.completedCount ?? 0,
    },
    {
      key: "status",
      header: "Status",
      render: (task) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            task.isActive ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
          }`}
        >
          {task.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (task) => {
        const index = tasks.findIndex((t) => t.id === task.id);
        return (
          <div className="flex flex-wrap gap-2">
            <button
              title="Edit"
              aria-label="Edit"
              className="flex items-center justify-center h-8 w-8 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition"
              onClick={(e) => { e.stopPropagation(); handleEdit(index); }}
            >
              <PencilSquareIcon className="h-4 w-4" />
            </button>
            <button
              title="Delete"
              aria-label="Delete"
              className="flex items-center justify-center h-8 w-8 bg-red-600 text-white rounded hover:bg-red-700 transition"
              onClick={(e) => { e.stopPropagation(); handleDelete(task.id); }}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
            <button
              title={task.isActive ? "Deactivate" : "Activate"}
              aria-label={task.isActive ? "Deactivate" : "Activate"}
              className={`flex items-center justify-center h-8 w-8 rounded text-white transition ${
                task.isActive ? "bg-gray-600 hover:bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={(e) => { e.stopPropagation(); toggleActive(task); }}
            >
              <PowerIcon className="h-4 w-4" />
            </button>
            <button
              title="History"
              aria-label="History"
              className="flex items-center justify-center h-8 w-8 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
              onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${task.id}/history`); }}
            >
              <ClockIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ];

  const completionColumns = [
    { key: "userEmail", header: "User" },
    { key: "taskTitle", header: "Task" },
    {
      key: "taskType",
      header: "Type",
      render: (item) => (
        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-300">
          {taskTypeLabel(item.taskType)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            item.status === "completed" ? "bg-emerald-500/20 text-emerald-300" : "bg-orange-500/20 text-orange-300"
          }`}
        >
          {item.status}
        </span>
      ),
    },
    { key: "rewardCoin", header: "Reward Coin" },
    {
      key: "completedAt",
      header: "Completed At",
      render: (item) => new Date(item.completedAt).toLocaleString(),
    },
  ];

  return (
    <div className="flex flex-col md:flex-row bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-6">
          <h2 className="text-3xl font-bold mb-6 text-white">Task Management</h2>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg w-max">
              <button
                onClick={() => setActiveTab("tasks")}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === "tasks" ? "bg-brand text-white" : "text-gray-300 hover:text-white hover:bg-gray-700"
                }`}
              >
                Tasks
              </button>
              <button
                onClick={() => setActiveTab("completions")}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === "completions" ? "bg-brand text-white" : "text-gray-300 hover:text-white hover:bg-gray-700"
                }`}
              >
                Completions
              </button>
            </div>
          </div>

          {activeTab === "tasks" ? (
            <>
              {/* Filters + Add button */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-6 bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  <div className="relative sm:w-64">
                    <input
                      type="text"
                      placeholder="Search tasks..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                  <select
                    value={taskTypeFilter}
                    onChange={(e) => setTaskTypeFilter(e.target.value)}
                    className="bg-gray-700 text-white rounded-lg px-3 py-2"
                  >
                    <option value="">All Types</option>
                    {TASK_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <select
                    value={isActiveFilter}
                    onChange={(e) => setIsActiveFilter(e.target.value)}
                    className="bg-gray-700 text-white rounded-lg px-3 py-2"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <button
                  onClick={() => {
                    setEditingIndex(null);
                    setFormData(emptyForm);
                    setShowModal(true);
                  }}
                  className="flex items-center justify-center gap-2 bg-brand text-white font-semibold px-4 py-2 rounded hover:bg-brand-dark transition"
                >
                  <PlusIcon className="h-5 w-5" />
                  Add Task
                </button>
              </div>

              {error && <p className="text-red-400 mb-4">{error}</p>}

              <Table
                columns={taskColumns}
                rows={tasks}
                rowKey={(task) => task.id}
                loading={loading}
                emptyMessage="No tasks found."
                onRowClick={(task) => viewTaskDetail(task.id)}
              />

              {!loading && tasks.length > 0 && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 px-4 py-3 bg-gray-800 rounded-lg gap-3">
                  <p className="text-sm text-gray-400">
                    Showing <span className="font-medium text-white">
                      {totalItems > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0}
                    </span> to{' '}
                    <span className="font-medium text-white">
                      {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)}
                    </span>{' '}
                    of <span className="font-medium text-white">{totalItems}</span> results
                  </p>
                  <Pagination page={currentPage} totalPages={totalPages} onPageChange={fetchTasks} />
                </div>
              )}
            </>
          ) : (
            <>
              {/* Completions filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6 bg-gray-800 p-4 rounded-lg shadow">
                <input
                  type="number"
                  placeholder="Filter by Task ID"
                  value={taskIdFilter}
                  onChange={(e) => setTaskIdFilter(e.target.value)}
                  className="bg-gray-700 text-white rounded-lg px-3 py-2 sm:w-48"
                />
                <input
                  type="number"
                  placeholder="Filter by User ID"
                  value={userIdFilter}
                  onChange={(e) => setUserIdFilter(e.target.value)}
                  className="bg-gray-700 text-white rounded-lg px-3 py-2 sm:w-48"
                />
              </div>

              {completionsError && <p className="text-red-400 mb-4">{completionsError}</p>}

              <Table
                columns={completionColumns}
                rows={completions}
                rowKey={(item) => item.id}
                loading={completionsLoading}
                emptyMessage="No task completions found."
              />

              <div className="mt-6">
                <Pagination page={completionsPage} totalPages={completionsTotalPages} onPageChange={fetchCompletions} />
              </div>
            </>
          )}

          {/* Add/Edit Modal */}
          <Modal
            isOpen={showModal}
            onClose={() => !submitting && setShowModal(false)}
            title={editingIndex !== null ? "Edit Task" : "Add New Task"}
            size="lg"
            footer={
              <>
                <button
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={editingIndex !== null ? handleSave : handleAddNew}
                  disabled={submitting}
                  className="bg-brand text-white px-4 py-2 rounded hover:bg-brand-dark transition disabled:opacity-50"
                >
                  {submitting
                    ? (editingIndex !== null ? "Saving..." : "Creating...")
                    : (editingIndex !== null ? "Save Changes" : "Add Task")}
                </button>
              </>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  placeholder="Title"
                  value={formData.title}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.title && <p className="text-red-400 text-sm">{formErrors.title}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <textarea
                  name="description"
                  placeholder="Description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Task Type</label>
                <select
                  name="taskType"
                  value={formData.taskType}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                >
                  <option value="">Select Task Type</option>
                  {TASK_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                {formErrors.taskType && <p className="text-red-400 text-sm">{formErrors.taskType}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Reward Coin</label>
                <input
                  type="number"
                  name="rewardCoin"
                  placeholder="Reward Coin"
                  value={formData.rewardCoin}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.rewardCoin && <p className="text-red-400 text-sm">{formErrors.rewardCoin}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Link</label>
                <input
                  type="text"
                  name="link"
                  placeholder="e.g. Telegram / website URL"
                  value={formData.link}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Video URL</label>
                <input
                  type="text"
                  name="videoUrl"
                  placeholder="Video URL"
                  value={formData.videoUrl}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
              </div>
              {formData.taskType === "website_visit" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Visit Duration (seconds)</label>
                  <input
                    type="number"
                    name="visitDuration"
                    placeholder="Visit Duration (seconds)"
                    value={formData.visitDuration}
                    onChange={handleChange}
                    className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                  />
                  {formErrors.visitDuration && <p className="text-red-400 text-sm">{formErrors.visitDuration}</p>}
                </div>
              )}
              <div className="sm:col-span-2">
                <ImageDropzone
                  file={formData.image}
                  onChange={handleImageChange}
                  existingImageUrl={editingIndex !== null ? resolveImage(tasks[editingIndex]?.image) : null}
                  error={formErrors.image}
                />
              </div>
            </div>
          </Modal>

          {/* Task Detail Modal */}
          <Modal
            isOpen={showDetailModal}
            onClose={closeDetailModal}
            title="Task Details"
            size="lg"
            footer={
              <button
                onClick={closeDetailModal}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition"
              >
                Close
              </button>
            }
          >
            {detailLoading ? (
              <div className="flex justify-center py-10">
                <Spinner size="lg" />
              </div>
            ) : taskDetail ? (
              <>
                <div className="flex items-start gap-4 mb-6">
                  {resolveImage(taskDetail.image) ? (
                    <img
                      src={resolveImage(taskDetail.image)}
                      alt={taskDetail.title}
                      className="h-24 w-24 object-cover rounded-lg border border-gray-700"
                    />
                  ) : (
                    <div className="h-24 w-24 flex items-center justify-center rounded-lg bg-gray-900 text-gray-500 text-xs italic">
                      No Image
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-white">{taskDetail.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-300">
                        {taskTypeLabel(taskDetail.taskType)}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          taskDetail.isActive ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
                        }`}
                      >
                        {taskDetail.isActive ? "Active" : "Inactive"}
                      </span>                                             
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                    <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">
                      {taskDetail.description || "-"}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Reward Coin</label>
                      <p className="text-lg font-semibold text-white bg-gray-900 px-3 py-2 rounded">
                        {taskDetail.rewardCoin}
                      </p>
                    </div>
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Completed Count</label>
                      <p className="text-lg font-semibold text-white bg-gray-900 px-3 py-2 rounded">
                        {taskDetail.completedCount ?? 0}
                      </p>
                    </div> */}
                    {taskDetail.taskType === "website_visit" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Visit Duration</label>
                        <p className="text-lg font-semibold text-white bg-gray-900 px-3 py-2 rounded">
                          {taskDetail.visitDuration}s
                        </p>
                      </div>
                    )}
                  </div>

                  {taskDetail.link && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Link</label>
                      <a
                        href={taskDetail.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-blue-400 underline bg-gray-900 px-3 py-2 rounded break-all"
                      >
                        {taskDetail.link}
                      </a>
                    </div>
                  )}

                  {taskDetail.videoUrl && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Video URL</label>
                      <a
                        href={taskDetail.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-blue-400 underline bg-gray-900 px-3 py-2 rounded break-all"
                      >
                        {taskDetail.videoUrl}
                      </a>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Created At</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">
                        {new Date(taskDetail.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Last Updated</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">
                        {taskDetail.updatedAt ? new Date(taskDetail.updatedAt).toLocaleString() : "Not updated"}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default TaskPage;
